import React, { useEffect, useState } from 'react';
import { Calendar, ExternalLink, History, Truck, X } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import config from '../config';

export const formatDate = (value) => (value ? new Date(value).toLocaleDateString('fr-BE') : '');
export const formatEur = (value) => `${parseFloat(value).toFixed(2).replace('.', ',')} €`;

// Odoo stores "/" for not-yet-numbered bills; show a readable fallback.
export const displayInvoiceNumber = (inv) => {
    const n = (inv.invoice_number || '').trim();
    return (!n || n === '/') ? `Brouillon · ${formatDate(inv.invoice_date)}`.trim() : n;
};

const lineProductName = (line) => (line.product_id ? line.product_id[1] : line.name) || 'Ligne sans nom';

/** One bill's lines; click a product to trace it across every bill it appeared on. */
const BillSheet = ({ bill, onClose }) => {
    const [billLines, setBillLines] = useState([]);
    const [loadingLines, setLoadingLines] = useState(true);

    // Product purchase-history modal (stacked on top of the bill modal)
    const [historyProduct, setHistoryProduct] = useState(null);
    const [historyRows, setHistoryRows] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    useEffect(() => {
        let current = true;
        setBillLines([]);
        setLoadingLines(true);
        axiosInstance.get(`invoices/${bill.id}/lines/`)
            .then(({ data }) => { if (current) setBillLines(Array.isArray(data) ? data : []); })
            .catch((error) => console.error('Failed to fetch invoice lines:', error))
            .finally(() => { if (current) setLoadingLines(false); });
        return () => { current = false; };
    }, [bill.id]);

    const openHistory = async (line) => {
        const product_id = line.product_id ? line.product_id[0] : null;
        const name = line.product_id ? line.product_id[1] : line.name;
        setHistoryProduct({ name: name || 'Produit sans nom' });
        setHistoryRows([]);
        setLoadingHistory(true);
        try {
            const params = product_id ? { product_id } : { name };
            const { data } = await axiosInstance.get('product-history/', { params });
            setHistoryRows(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to fetch product history:', error);
        } finally {
            setLoadingHistory(false);
        }
    };

    return (
        <>
        {/* Bill detail modal */}
        {bill && (
            <div className="sheet-overlay" onClick={onClose}>
                <div className="sheet sheet-flush" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-start justify-between p-5 border-b border-border shrink-0">
                        <div className="flex items-center gap-3">
                                                            <div>
                                <h2 className="text-xl leading-tight text-foreground">{displayInvoiceNumber(bill)}</h2>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
                                    <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{bill.supplier_name}</span>
                                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(bill.invoice_date)}</span>
                                    <span className="font-semibold text-foreground">{formatEur(bill.total_amount)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            {config.odooUrl && (
                                <a
                                    href={`${config.odooUrl}/odoo/vendor-bills/${bill.odoo_invoice_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 hover:bg-accent rounded-lg text-muted-foreground hover:text-primary"
                                    title="Ouvrir dans Odoo"
                                >
                                    <ExternalLink className="h-4 w-4" />
                                </a>
                            )}
                            <button onClick={onClose} className="p-2 hover:bg-accent rounded-lg">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    <div className="p-5 overflow-y-auto flex-1">
                        <p className="text-[11px] font-medium text-muted-foreground mb-2">
                            {loadingLines ? 'Lignes' : `${billLines.length} lignes`}. Touchez un produit pour voir ses achats.
                        </p>
                        <div className="rounded-lg border border-border overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/50 text-[11px] text-muted-foreground">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Produit</th>
                                        <th className="hidden sm:table-cell px-3 py-2 text-right">Qté</th>
                                        <th className="hidden sm:table-cell px-3 py-2 text-right">Prix</th>
                                        <th className="px-3 py-2 text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {loadingLines ? (
                                        <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground italic text-xs">Chargement depuis Odoo…</td></tr>
                                    ) : billLines.length === 0 ? (
                                        <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground italic text-xs">Aucune ligne.</td></tr>
                                    ) : (
                                        billLines.map((line, idx) => (
                                            <tr key={idx} className="hover:bg-muted/30">
                                                <td className="px-3 py-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => openHistory(line)}
                                                        className="text-left text-foreground hover:text-primary hover:underline inline-flex items-center gap-1"
                                                        title="Voir les achats de ce produit"
                                                    >
                                                        <History className="h-3 w-3 opacity-60" />
                                                        {lineProductName(line)}
                                                    </button>
                                                </td>
                                                <td className="hidden sm:table-cell px-3 py-2 text-right text-muted-foreground whitespace-nowrap">
                                                    {line.quantity}{line.product_uom_id ? ` ${line.product_uom_id[1]}` : ''}
                                                </td>
                                                <td className="hidden sm:table-cell px-3 py-2 text-right text-muted-foreground whitespace-nowrap">{formatEur(line.price_unit)}</td>
                                                <td className="px-3 py-2 text-right price whitespace-nowrap">{formatEur(line.price_subtotal)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <p className="mt-3 text-[11px] text-muted-foreground italic">
                            Importée le {bill.synced_at ? new Date(bill.synced_at).toLocaleString('fr-BE') : '—'}
                        </p>
                    </div>
                </div>
            </div>
        )}

        {/* Product purchase-history modal */}
        {historyProduct && (
            <div className="sheet-overlay !z-[60]" onClick={() => setHistoryProduct(null)}>
                <div className="sheet sheet-flush" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-start justify-between p-5 border-b border-border shrink-0">
                        <div className="flex items-center gap-3">
                                                            <div>
                                <h2 className="text-xl leading-tight text-foreground">{historyProduct.name}</h2>
                                <p className="text-xs text-muted-foreground mt-0.5">Toutes les factures où ce produit apparaît</p>
                            </div>
                        </div>
                        <button onClick={() => setHistoryProduct(null)} className="p-2 hover:bg-accent rounded-lg">
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="p-5 overflow-y-auto flex-1">
                        <div className="rounded-lg border border-border overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/50 text-[11px] text-muted-foreground">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Date</th>
                                        <th className="hidden sm:table-cell px-3 py-2 text-left">Fournisseur</th>
                                        <th className="px-3 py-2 text-left">Facture</th>
                                        <th className="hidden sm:table-cell px-3 py-2 text-right">Qté</th>
                                        <th className="px-3 py-2 text-right">Prix</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {loadingHistory ? (
                                        <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground italic text-xs">Recherche dans Odoo…</td></tr>
                                    ) : historyRows.length === 0 ? (
                                        <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground italic text-xs">Aucune autre facture pour ce produit.</td></tr>
                                    ) : (
                                        historyRows.map((r, idx) => (
                                            <tr key={idx} className="hover:bg-muted/30">
                                                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{formatDate(r.date) || '—'}</td>
                                                <td className="hidden sm:table-cell px-3 py-2 text-foreground">{r.partner_id ? r.partner_id[1] : '—'}</td>
                                                <td className="px-3 py-2 text-muted-foreground">{r.move_id ? r.move_id[1] : '—'}</td>
                                                <td className="hidden sm:table-cell px-3 py-2 text-right text-muted-foreground whitespace-nowrap">
                                                    {r.quantity}{r.product_uom_id ? ` ${r.product_uom_id[1]}` : ''}
                                                </td>
                                                <td className="px-3 py-2 text-right price whitespace-nowrap">{formatEur(r.price_unit)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {historyRows.length > 0 && (
                            <p className="mt-3 text-[11px] text-muted-foreground">
                                {historyRows.length} achat(s), le plus récent d’abord
                            </p>
                        )}
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default BillSheet;
